// 个人的 profile
import React, {useContext, useEffect, useState} from "react";
import "./profile.css";
import CustomPopup from "../../../../components/CustomPopup";
import {ThemeContext} from "../../../../providers/themeContext/theme-context.tsx";
import {useUserContext} from "../../../../providers/userProvider/user-context.tsx";
import {ToastContext} from "../../../../components/CustomToast/ToastProvider.tsx";
import {GAEvents} from "../../../../common/utils/GA4/GAEvents.ts";
import {PageEnum} from "../../../../types/IPage.ts";
import {UrlConfig} from "../../../../common/config/url-config.ts";
import useLoading from "../../../../providers/loadingContext/useLoading.ts";
import {IMedal, IUserGame, LoginType} from "../../../../common/utils/http/IResponse.ts";
import {apiService} from "../../../../common/utils/http/api-service.ts";
import SkinsPage from "../skins";
import CountrySelectionDialog from "../countrySelectDialog/CountrySelectionDialog.tsx";
import AvatarFrameDialog from "../AvatarFrameDialog/AvatarFrameDialog.tsx";
import Avatar from "../../../../components/Avatar/Avatar.tsx";
import {useMusicContext} from "../../../../providers/musicProvider/music-player-context.tsx";
import {LocalStorageKey} from "../../../../types/IGameStat.ts";
import {JudgmentPlatform} from "../../../../common/utils/checkPlatform.ts";

type ProfileModalProps = {
    isOpen: boolean;
    onClose: () => void;
    switchPage: (page: PageEnum) => void;
    onShowMedalDialog: () => void;
    equippedMedalList: IMedal[];
    loadEquippedMedalList: () => void;
    onOpenLoginModal: () => void;
    gameData?: IUserGame;
    setGameData: (gameData: IUserGame) => void;
}

const ProfilePage: React.FC<ProfileModalProps> = ({
                                                      isOpen,
                                                      onClose,
                                                      switchPage,
                                                      onShowMedalDialog,
                                                      equippedMedalList,
                                                      loadEquippedMedalList,
                                                      gameData,
                                                      setGameData,
                                                      onOpenLoginModal
                                                  }) => {
    const {showLoading, hideLoading} = useLoading();
    // 全局用户信息provider
    const {user, clearUser} = useUserContext();
    // 全局音乐provider
    const musicManager = useMusicContext();
    // 主题管理器
    const {imgUrls} = useContext(ThemeContext);
    const {addToast} = useContext(ToastContext);
    // 切换按钮
    const [switchIndex, setSwitchIndex] = useState<number>(0)
    // 是否显示国家选择弹窗
    const [isShowCountryDialog, setIsShowCountryDialog] = useState<boolean>(false)
    // 是否显示头像框选择弹窗
    const [isShowAvatarFrameDialog, setIsShowAvatarFrameDialog] = useState<boolean>(false)

    useEffect(() => {
        if (isOpen) {
            // GA埋点
            GAEvents.profileShow()
            // 加载游戏数据
            loadGameData();
            if (user?.loginType === LoginType.CGTN) {
                loadEquippedMedalList();
            }
        }
    }, [isOpen]);

    // 加载游戏数据
    const loadGameData = async () => {
        try {
            showLoading();
            const res = await apiService.getProfileGameData();
            if (res.code === 200 && res.data) {
                setGameData(res.data);
            }
        } catch (e) {
            console.log(e?.toString())
            addToast({message: `Failed to load game data.`, type: "error"});
        } finally {
            hideLoading();
        }
    }


    // 退出登录
    const logout = async () => {
        try {
            showLoading()
            musicManager.playButtonSound()
            await apiService.logout();
            clearUser();
            localStorage.removeItem(LocalStorageKey.TOKEN)
            if(!JudgmentPlatform.isCGTNAPPAutoJudge()){
                window.open(`${UrlConfig.SING_OUT_URL}${UrlConfig.CALL_BACK_URL}`);
            }
            switchPage(PageEnum.LOGIN);
        } finally {
            hideLoading()
        }
    };

    // 切换按钮
    const handleSwitchIndex = (index: number) => {
        if (index === 0) {
            // GA埋点
            GAEvents.profileInfoClick()
        }
        if (index === 1) {
            // GA埋点
            GAEvents.profileSkinClick()
        }
        musicManager.playButtonSound()
        setSwitchIndex(index)
    }

    // 打开/关闭国家选择弹窗
    const handleShowCountryDialog = (open: boolean) => {
        if (open) {
            // GA埋点
            GAEvents.profileCountryClick()
        }
        musicManager.playButtonSound()
        setIsShowCountryDialog(open)
    }

    // 打开Medal勋章弹窗
    const openMedalDialog = () => {
        // GA埋点
        GAEvents.profileAchieveClick()
        // 关闭profile弹窗
        // onClose();
        // 打开Medal勋章弹窗
        musicManager.playButtonSound()
        onShowMedalDialog();
    }

    // 显示头像框弹窗
    const showAvatarFrameDialog = (open: boolean) => {
        musicManager.playButtonSound()
        setIsShowAvatarFrameDialog(open)
    }

    // 切换到登录页面
    const handleSwitchToLogin = () => {
        switchPage(PageEnum.LOGIN)
    }

    return (
        <CustomPopup isOpen={isOpen} onClose={onClose}>
            <div className="my-profile-page-container">
                <img className="profile-background-img" src={imgUrls.profileBgPic} alt="img"/>
                <div className="profile-header">
                    <div className="profile-title my-roboto-medium">My Profile</div>
                    <img onClick={onClose} className="profile-close-btn"
                         src={imgUrls.closeBtnWhiteUrl}
                         alt="close"/>
                </div>
                <div
                    onClick={user?.loginType === LoginType.GUEST ? onOpenLoginModal : () => showAvatarFrameDialog(true)}
                    className="height-66 width-66 margin-left-right-auto margin-top-20 position-relative">
                    <Avatar className="height-66 width-66 padding-all-2 bg-color-white border-radius-round"/>
                    <img className="height-17 width-17 position-absolute position-bottom-0 right-minus-4"
                         src={imgUrls.profileAvatarLittleSwitchIcon} alt="pic"/>
                </div>
                <div className="user-name my-roboto-medium">
                    {user?.loginType === LoginType.GUEST ? user?.guestName : user?.cgtnName ?? "Guest_01"}
                </div>
                {/*国家选择*/}
                <div onClick={() => handleShowCountryDialog(true)}
                     className="margin-top-3 border-radius-34 padding-top-3 display-flex-row display-flex-justify-content-center display-flex-align-items-center padding-bottom-3 bg-light-blue height-22 min-width-104 max-width-310 margin-left-right-auto text-center font-size-14 color-blue my-roboto-regular">
                    <div className="max-width-290 margin-left-8 text-ellipsis ">
                        {user?.country ?? "Country/Region"}
                    </div>
                    <div className="width-21">{`>`}</div>
                </div>
                {/*切换按钮*/}
                <div
                    className="margin-top-8 width-333 height-29 border-radius-6 bg-color-grey margin-left-right-auto display-flex-row">
                    <div onClick={() => handleSwitchIndex(0)}
                         className={`${switchIndex === 0 ? 'bg-color-blue-btn color-white' : ''} width-50-percent height-100-percent my-roboto-medium font-weight-500 display-flex-row display-flex-justify-content-center display-flex-align-items-center border-left-radius-6 font-size-14`}>
                        Profile Info
                    </div>
                    <div onClick={() => handleSwitchIndex(1)}
                         className={`${switchIndex === 1 ? 'bg-color-blue-btn color-white' : ''} width-50-percent height-100-percent my-roboto-medium font-weight-500 display-flex-row display-flex-justify-content-center display-flex-align-items-center border-right-radius-6 font-size-14`}>
                        Skins
                    </div>
                </div>
                {switchIndex === 0 ?
                    <div>
                        <div className="stats-main-container margin-left-right-auto">
                            <div
                                className="margin-top-6 display-flex-row display-flex-align-items-center display-flex-justify-content-center">
                                <div className="grid-item margin-top-bottom-auto">
                                    <div className="font-size-20 my-roboto-bold">{gameData?.totalGamePlayed ?? 0}</div>
                                    <div className="subtitle my-roboto-regular">Total</div>
                                </div>
                                <div className="grid-item margin-left-6">
                                    <div
                                        className="font-size-20 my-roboto-bold">{gameData?.averageGuess ?? 0}</div>
                                    <div className="subtitle my-roboto-regular">Average Guess</div>
                                </div>
                            </div>
                            <div
                                className="margin-top-6 display-flex-row display-flex-align-items-center display-flex-justify-content-center">

                                <div className="grid-item">
                                    <div className="font-size-20 my-roboto-bold">{gameData?.dailyGameWinNum ?? 0}</div>
                                    <div className="subtitle my-roboto-regular">Wins</div>
                                </div>
                                <div className="grid-item margin-left-6">
                                    <div
                                        className="font-size-20 my-roboto-bold">{gameData?.aiChallengeBestStreak ?? 0}</div>
                                    <div className="subtitle my-roboto-regular">Best Streak</div>
                                </div>
                            </div>
                        </div>
                        {/*徽章列表部分*/}
                        <div
                            className="position-relative margin-top-6 border-radius-10 height-175 width-333 bg-light-green-container margin-left-right-auto">
                            <div className="font-size-16 my-roboto-bold text-center padding-top-14">Achievements</div>
                            <div onClick={user?.loginType === LoginType.CGTN ? openMedalDialog : onOpenLoginModal}
                                 className="position-absolute position-right-16 position-top-17 my-roboto-regular color-blue font-size-14">{`More >`}</div>
                            {
                                user?.loginType === LoginType.GUEST ?
                                    <div
                                        className="height-119 bg-color-white-70-opacity border-radius-6 width-306 margin-top-10 margin-left-right-auto display-flex-row">

                                        <div
                                            className="width-240 margin-left-right-auto display-flex-column display-flex-justify-content-center display-flex-align-items-center">
                                            <div className="my-roboto-regular color-grey font-size-16 ">Log in to see
                                                more
                                            </div>
                                            <div onClick={handleSwitchToLogin}
                                                 className="margin-top-14 height-29 width-93 text-center bg-color-blue-btn color-white my-roboto-medium font-size-14 display-flex-row display-flex-align-items-center display-flex-justify-content-center border-radius-34">Log
                                                In
                                            </div>
                                        </div>
                                    </div>
                                    :
                                    <div
                                        className="height-119 width-306 margin-top-10 margin-left-right-auto display-flex-row">
                                        {equippedMedalList.length > 0 ?
                                            <div onClick={openMedalDialog}
                                                 className="display-flex-column border-radius-6 bg-color-white width-97 height-119 display-flex-align-items-center display-flex-justify-content-center">
                                                <img src={equippedMedalList[0].medalUrl}
                                                     className="height-73 width-73 object-fit-contain" alt="pic"/>
                                                <div
                                                    className="margin-top-3 font-size-14 my-roboto-medium text-center">{equippedMedalList[0].medalName}</div>
                                            </div>
                                            : <div onClick={openMedalDialog}
                                                   className="display-flex-column border-radius-6 bg-color-white width-97 height-119 margin-left-7 display-flex-justify-content-center display-flex-align-items-center">
                                                <img src={imgUrls.medalAddIconGrey} className="height-73 width-73"
                                                     alt="pic"/>
                                                <div className="margin-top-3 font-size-13 my-roboto-regular">Add</div>
                                            </div>}
                                        {equippedMedalList.length > 1 ? <div onClick={openMedalDialog}
                                                                             className="display-flex-column border-radius-6 bg-color-white width-97 height-119 margin-left-7 display-flex-justify-content-center display-flex-align-items-center">
                                            <img src={equippedMedalList[1].medalUrl}
                                                 className="height-73 width-73 object-fit-contain" alt="pic"/>
                                            <div
                                                className="margin-top-3 font-size-14 my-roboto-medium  text-center">{equippedMedalList[1].medalName}</div>
                                        </div> : <div onClick={openMedalDialog}
                                                      className="display-flex-column border-radius-6 bg-color-white width-97 height-119 margin-left-7 display-flex-justify-content-center display-flex-align-items-center">
                                            <img src={imgUrls.medalAddIconGrey} className="height-73 width-73"
                                                 alt="pic"/>
                                            <div className="margin-top-3 font-size-13 my-roboto-regular">Add</div>
                                        </div>}
                                        {equippedMedalList.length > 2 ? <div onClick={openMedalDialog}
                                                                             className="display-flex-column border-radius-6 bg-color-white width-97 height-119 margin-left-7 display-flex-justify-content-center display-flex-align-items-center">
                                                <img src={equippedMedalList[2].medalUrl}
                                                     className="height-73 width-73 object-fit-contain" alt="pic"/>
                                                <div
                                                    className="margin-top-3 font-size-14 my-roboto-medium text-center">{equippedMedalList[2].medalName}</div>
                                            </div> :
                                            <div onClick={openMedalDialog}
                                                 className="display-flex-column border-radius-6 bg-color-white width-97 height-119 margin-left-7 display-flex-justify-content-center display-flex-align-items-center">
                                                <img src={imgUrls.medalAddIconGrey} className="height-73 width-73"
                                                     alt="pic"/>
                                                <div className="margin-top-3 font-size-13 my-roboto-regular">Add</div>
                                            </div>}
                                    </div>}
                        </div>
                        {user?.loginType === LoginType.CGTN ?
                            <div onClick={logout}
                                 className="display-flex-row display-flex-justify-content-center logout-btn my-roboto-regular">
                                <img className="height-16 width-16 margin-right-4 margin-left-4"
                                     src={imgUrls.logoutIconUrl}
                                     alt="pic"/>
                                Log Out
                            </div> : undefined}
                    </div> :
                    <SkinsPage switchPage={switchPage} switchIndex={switchIndex} isOpened={isOpen}/>}
            </div>
            {/*国家选择弹窗*/}
            <CountrySelectionDialog isOpen={isShowCountryDialog} onClose={() => handleShowCountryDialog(false)}/>
            {/*头像框选择弹窗*/}
            <AvatarFrameDialog isOpen={isShowAvatarFrameDialog} onClose={() => showAvatarFrameDialog(false)}/>
        </CustomPopup>
    );
}

export default ProfilePage;